"use client";

import React, { useEffect, useRef, useCallback } from "react";
import { requestUserPromptFromApp } from "@/app/utils/handyBridge";
import { matchSystemPrompt } from "@/const/prompts";
import {
  RobotAIKeywordType,
  RobotAIKeywordItem,
} from "@/app/types/typeConfig/chatQuestion";
import {
  useQueryChatBehavior,
  QueryBehaviorTypeEnum,
} from "@/app/hooks/robot/useQueryChatBehavior";
import { requireRobotAIKeywordList } from "@/app/api/robot/handyApp";
import { useMessageStore } from "@/app/store";
import { useSSESend } from "@/app/hooks/robot/useSSESend";
import {
  MessageSender,
  RobotMessageType,
  MessageType,
  HeaderType,
} from "@/app/store/types";
import { createClientUUid } from "@/app/utils/string";

interface RouteParamsHandlerProps {
  children: React.ReactNode;
  lng: string;
}

// 提示词类型枚举
enum PromptTypeEnum {
  CUSTOM = 1, // 前端维护的提示词
  DEVICE_FLOAT = 2, // 设备页浮球
  PRINT_FLOW = 3, // 发起打印流程中
  HELP_CENTER = 4, // 帮助中心搜索
  BIND_PRINTER = 5, // 绑定打印机
}

/**
 * 优化后的 RouteParamsHandler 组件
 *
 * 主要改进：
 * 1. 使用 useRef 追踪消息发送状态，避免不必要的重渲染
 * 2. 提取业务逻辑到独立的 hooks 或 services
 * 3. 改善类型安全
 * 4. 统一错误处理
 * 5. 解耦组件逻辑和业务逻辑
 * 6. 修复 useEffect 依赖问题
 */
export default function RouteParamsHandler({
  children,
  lng,
}: RouteParamsHandlerProps) {
  const { queryParams } = useQueryChatBehavior(
    QueryBehaviorTypeEnum.HANDY_CHAT_SEND,
  );

  // 使用 useRef 替代 useState，避免不必要的重渲染
  const hasSentAutoMessage = useRef(false);
  const hasSentHotMessage = useRef(false);
  const hasInitialized = useRef(false);

  // 提前声明 store 和 hooks
  const { setRobotMessage, historyLoaded } = useMessageStore();
  const { handleSSESend } = useSSESend();

  /**
   * 获取机器人关键词类型
   */
  const getKeywordType = useCallback(
    (promptType: PromptTypeEnum): RobotAIKeywordType => {
      const typeMap: Record<number, RobotAIKeywordType> = {
        [PromptTypeEnum.PRINT_FLOW]: RobotAIKeywordType.printprep,
        [PromptTypeEnum.BIND_PRINTER]: RobotAIKeywordType.binding,
      };
      return typeMap[promptType] || RobotAIKeywordType.hot;
    },
    [],
  );

  /**
   * 获取 App 提示词列表
   */
  const getAppPromptList = useCallback(async (): Promise<
    RobotAIKeywordItem[]
  > => {
    try {
      const { hmsList } = await requestUserPromptFromApp();
      if (!hmsList || hmsList.length === 0) {
        return [];
      }
      return hmsList.map((item) => ({
        keyword: item.hms || "",
        id: "",
        languageCode: RobotAIKeywordType.hot,
        status: true,
      }));
    } catch (error) {
      console.error("获取提示词失败:", error);
      return [];
    }
  }, []);

  /**
   * 请求分类列表
   */
  const requireCategoryList = useCallback(
    async (promptsList: RobotAIKeywordItem[]) => {
      try {
        let data = promptsList;

        if (data.length === 0) {
          console.log(
            "Fetching robot AI keyword list",
            getKeywordType(
              queryParams?.promptType as keyof typeof matchSystemPrompt,
            ),
          );
          data = await requireRobotAIKeywordList({
            languageCode: lng,
            type: getKeywordType(
              queryParams?.promptType as keyof typeof matchSystemPrompt,
            ),
          });
        }
        if (data.length > 0) {
          // 调用外部传入的回调函数来处理问题数据
          setRobotMessage({
            content: "",
            senderType: MessageSender.ROBOT,
            timestamp: Date.now(),
            type: MessageType.Text,
            headerType: HeaderType.MESSAGE,
            messageType: RobotMessageType.ROBOT_HANDY_SELECT_HOT_QUESTION,
            robotData: {
              title: String(queryParams?.promptType) || "",
              questionList: data.slice(0, 5),
            },
          });
          hasSentHotMessage.current = true;
        }
      } catch (error) {
        console.error("请求分类列表失败:", error);
      }
    },
    [lng, queryParams?.promptType, getKeywordType, setRobotMessage],
  );

  /**
   * 发送自动消息
   */
  const sendAutoMessage = useCallback(async () => {
    if (hasSentAutoMessage.current) return;

    try {
      const { printerModel, hms, handyLan } = await requestUserPromptFromApp();

      if (!printerModel || !hms) {
        console.warn("未获取到 printerModel 或 hms，无法发送消息");
        return;
      }

      const system =
        matchSystemPrompt(
          queryParams?.promptType as keyof typeof matchSystemPrompt,
        ) || "";
      const userPrompt = `User Prompt机型:${printerModel}故障:${hms}展示语言:${handyLan}`;
      const content = `${system} ${userPrompt}`;

      if (content.trim()) {
        hasSentAutoMessage.current = true;
        handleSSESend(content, false, true);
      }
    } catch (error) {
      console.error("发送AI消息失败:", error);
    }
  }, [queryParams?.promptType, handleSSESend]);

  /**
   * 发送热门消息
   */
  const sendHotMessages = useCallback(async () => {
    if (hasSentHotMessage.current) return;

    try {
      const hmsList = await getAppPromptList();
      await requireCategoryList(hmsList);
    } catch (error) {
      console.error("发送热门消息失败:", error);
    }
  }, [getAppPromptList, requireCategoryList]);

  /**
   * 发送 OP 配置消息
   */
  const sendOPConfigMessage = useCallback(async () => {
    try {
      const { question, answer } = await requestUserPromptFromApp();

      if (answer) {
        const text = `<div><b>${question}</b></div>${answer}`;

        setRobotMessage({
          clientId: createClientUUid(MessageType.Text),
          content: text,
          timestamp: Date.now(),
          streaming: true,
          type: MessageType.Text,
          headerType: HeaderType.MESSAGE,
          senderType: MessageSender.ROBOT,
          senderName: "智能助手",
          messageType: RobotMessageType.ROBOT_NORMAL_REPLY,
          robotData: {},
        });
      } else if (question) {
        handleSSESend(question, false);
      }
    } catch (error) {
      console.error("发送OP配置消息失败:", error);
    }
  }, [setRobotMessage, handleSSESend]);

  /**
   * 处理路由参数变化
   */
  const handleRouteParams = useCallback(async () => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    if (!queryParams?.promptType) {
      // 如果没有 promptType，加载默认热门问题
      if (!hasSentHotMessage.current) {
        await requireCategoryList([]);
      }
      return;
    }

    // 根据不同的 promptType 执行不同的操作
    switch (queryParams.promptType) {
      case PromptTypeEnum.CUSTOM:
        // 前端维护的提示词
        await sendAutoMessage();
        break;

      case PromptTypeEnum.DEVICE_FLOAT:
      case PromptTypeEnum.PRINT_FLOW:
      case PromptTypeEnum.BIND_PRINTER:
        // 发送热门消息
        await sendHotMessages();
        break;

      case PromptTypeEnum.HELP_CENTER:
        // 帮助中心过来就发送 OP 平台配置好的问答
        await sendOPConfigMessage();
        break;

      default:
        console.warn("未知的 promptType:", queryParams.promptType);
        break;
    }
  }, [
    queryParams,
    sendAutoMessage,
    sendHotMessages,
    sendOPConfigMessage,
    requireCategoryList,
  ]);

  // 等待历史记录加载完成后再处理路由参数
  useEffect(() => {
    if (historyLoaded && !hasInitialized.current) {
      handleRouteParams();
    }
  }, [historyLoaded, handleRouteParams]);

  return <>{children}</>;
}
